import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { adminLogin, setAdminToken, getAdminToken } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (getAdminToken()) return <Navigate to="/admin/blog" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const { token, expires_at } = await adminLogin(email.trim(), password.trim());
      setAdminToken(token, expires_at);
      toast.success("Welcome back.");
      nav("/admin/blog", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many attempts. Please wait a few minutes and try again.");
      } else if (status === 401) {
        toast.error("Invalid email or password.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="bg-white min-h-[70vh] flex items-center">
      <div className="max-w-md w-full mx-auto px-6 py-20">
        <div className="flex items-center gap-3 text-[#3A7A8C]">
          <Lock size={18} strokeWidth={1.6} />
          <span className="ash-eyebrow !text-[#3A7A8C]">Admin Access</span>
        </div>
        <h1 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-[#080808]">
          Sign in to the blog console.
        </h1>
        <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-6">
          <div>
            <label htmlFor="admin-email" className="ash-label">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="ash-input"
              data-testid="admin-email-input"
              placeholder="you@ashlanclinic.com"
            />
          </div>
          <div>
            <label htmlFor="admin-pw" className="ash-label">
              Password
            </label>
            <input
              id="admin-pw"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="ash-input"
              data-testid="admin-password-input"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="ash-btn-primary self-start disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </main>
  );
}
